import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { getDateAtLastSecond, getDateWithoutTime } from '../../../../helpers/dateHelpers';

import {
  useRentalsRelayQuery,
  mapRelayRentalsToIRentals,
  type RentalQueryFilter,
  type RentalSortInput,
} from '../../../../hooks/rentals/query/V2';
import useSearchDebounce from '../../../../hooks/search/useSearchDebounce';
import useExportRentals, {
  useExportSystemRentalsRoutes,
} from '../../../../hooks/rentals/query/useExportRentals';
import { IMode } from '../../../../ts/interface/mode.interface';
import { ISystem } from '../../../../ts/interface/system.interface';
import {
  IRental,
  IUseRentalQueryParameters,
} from '../../../../ts/interface/rental.interface';
import ShareIcon from '@mui/icons-material/Share';
import { history } from '../../../../utils';
import { PaginationTable } from '../pagination-table';
import {
  IColumn,
  ISortConfig,
} from '../../../../ts/interface/components/redesing/table.interface';
import Card from '@mui/material/Card';
import { SearchChip } from '../../../../ts/interface/components/table.interface';
import AddIcon from '@mui/icons-material/Add';
import {
  MovaticFieldGroupDate,
  MovaticFieldGroupMultiSelect,
} from '../../movatic-field-group';
import CustomAsyncToast from '../../custom-async-toast';
import CustomToast from '../../custom-toast';
import useQueryString from '../../../../hooks/queryParams/useQueryString';
import { DEFAULT_ROWS_PER_PAGE } from '../../../../constants';
import useUrlState from '../../../../hooks/use-url-state';
import { makeColumns } from '../../../../tableColumns/rental-columns';
import useCurrentSystemInfo from '../../../../hooks/system/hook/useCurrentSystemInfo';
import OrganizationSystemMultiSelect from '../../../system/organization-system-multi-select';
import { useKeysetPagination } from '../../../../hooks/use-keyset-pagination';

const filterOptions = [
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending_review', label: 'Pending review' },
];

type SystemOption = { value: string; label: string; original: ISystem };

const RentalTable = ({
  systemLoaded,
  parameters,
  onAddRental,
  system_id,
}: {
  systemLoaded: boolean;
  tabs?: JSX.Element;
  csvExportFileName?: string;
  noRentalsText: string;
  disableFetch?: boolean;
  onAddRental?: Function;
  parameters?: IUseRentalQueryParameters;
  header?: string;
  system_id: string;
}) => {
  const isOrgMode = Boolean(parameters?.organization_id);

  const { modeInfo } = useCurrentSystemInfo(
    isOrgMode ? null : parameters?.system_id || system_id
  );

  const { queryParams, deleteQueryParam } = useQueryString();

  // Shared server-side keyset pagination (page# kept local, not in the URL).
  const {
    pageNumber,
    after,
    isPaginating,
    onPageChange,
    onPageLoaded,
    reset: resetPagination,
  } = useKeysetPagination();

  // Pending (unapplied) filter selections shown in the filter drawer.
  const [componentsSelectedFilters, setComponentsSelectedFilters] = useState<any[]>(
    queryParams.get('status') ? [{ label: 'Active', value: 'active' }] : []
  );
  const [preSelectedStartDate, setPreSelectedStartDate] = useState('');
  const [preSelectedEndDate, setPreSelectedEndDate] = useState('');
  const [preSelectedSystems, setPreSelectedSystems] = useState<SystemOption[]>([]);

  const [pageState, setPageState, isComplete, isRestoring] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    status: queryParams.get('status') ? [queryParams.get('status') as string] : [],
    search: '',
    startDate: '',
    endDate: '',
    // Applied org-mode system selection (persisted so chips + query stay in sync).
    selectedSystems: [] as SystemOption[],
    sortConfig: {
      key: 'start_time',
      direction: 'desc',
    } as ISortConfig,
  });
  const debouncedSearchTerm = useSearchDebounce(pageState.search);

  const selectedSystems = pageState.selectedSystems;

  const isEnabled = isOrgMode
    ? isComplete || Boolean(parameters?.organization_id)
    : isComplete || systemLoaded;

  // Build GraphQL filter from applied page state and parameters
  const graphqlFilter = useMemo((): RentalQueryFilter | undefined => {
    const filter: RentalQueryFilter = {};

    // System filter
    if (isOrgMode && selectedSystems.length > 0) {
      filter.systemIds = selectedSystems.map((s) => s.value);
    } else if (parameters?.system_id) {
      filter.systemId = parameters.system_id;
    } else if (parameters?.organization_id) {
      filter.organizationId = parameters.organization_id;
    } else if (system_id) {
      filter.systemId = system_id;
    }

    // User filter
    if (parameters?.user_id) {
      filter.userId = parameters.user_id;
    }

    // Station filter
    if (parameters?.station_id) {
      filter.stationId = parameters.station_id;
    }

    // Coupon filter
    if (parameters?.coupon_id) {
      filter.couponId = parameters.coupon_id;
    }

    // Status filter - convert 'active' / 'completed' to completed boolean
    if (pageState.status?.length > 0) {
      if (
        pageState.status.includes('active') &&
        !pageState.status.includes('completed')
      ) {
        filter.completed = false;
      } else if (
        pageState.status.includes('completed') &&
        !pageState.status.includes('active')
      ) {
        filter.completed = true;
      }
      // If both are selected, don't filter by completed

      // 'pending_review' maps to the reviewStatus field, independent of the
      // completed boolean above (so it can be combined with active/completed).
      if (pageState.status.includes('pending_review')) {
        filter.reviewStatus = 'pending';
      }
    }

    // Search pattern
    if (debouncedSearchTerm) {
      filter.pattern = debouncedSearchTerm;
    }

    // Date filters
    if (pageState.startDate) {
      filter.startTimeAfter = getDateWithoutTime(
        new Date(pageState.startDate)
      ).toISOString();
    }
    if (pageState.endDate) {
      filter.startTimeBefore = getDateAtLastSecond(
        new Date(pageState.endDate)
      ).toISOString();
    }

    return Object.keys(filter).length > 0 ? filter : undefined;
  }, [
    isOrgMode,
    selectedSystems,
    system_id,
    parameters?.system_id,
    parameters?.organization_id,
    parameters?.user_id,
    parameters?.station_id,
    parameters?.coupon_id,
    pageState.status,
    pageState.startDate,
    pageState.endDate,
    debouncedSearchTerm,
  ]);

  // Build GraphQL sort from page state
  const graphqlSort = useMemo((): RentalSortInput[] => {
    const sortKeyMap: Record<string, string> = {
      start_time: 'START_TIME',
      end_time: 'END_TIME',
      endTime: 'END_TIME',
      duration: 'DURATION',
      charge_amount: 'CHARGE_AMOUNT',
      created_on: 'CREATED_ON',
    };

    const field = sortKeyMap[pageState.sortConfig?.key || 'start_time'] || 'START_TIME';
    const order = pageState.sortConfig?.direction === 'asc' ? 'ASC' : 'DESC';

    return [{ field: field as any, order: order as any }];
  }, [pageState.sortConfig?.key, pageState.sortConfig?.direction]);

  const {
    data: rentalsDataGraphQL,
    isRefetching,
    isLoading,
    count,
    hasMore: graphqlHasMore,
    endCursor,
  } = useRentalsRelayQuery(graphqlFilter, {
    limit: pageState.sizePerPage,
    sort: graphqlSort,
    enabled: isEnabled,
    after,
  });

  // Only the current page's rows are shown (server-side pagination).
  const rentalsItems = useMemo(
    () =>
      rentalsDataGraphQL
        ? (mapRelayRentalsToIRentals(rentalsDataGraphQL) as IRental[])
        : [],
    [rentalsDataGraphQL]
  );

  // Record the next page's cursor and clear the paginating loader when a fetch
  // settles.
  useEffect(() => {
    onPageLoaded(endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rentalsDataGraphQL]);

  const [chips, setChips] = useState<SearchChip[]>([]);

  // Rebuild the full chip list from the *applied* filters (the same
  // URL-persisted fields that drive the query), so the chips always match the
  // fetched data — including after a refresh and including when a filter was
  // cleared (no chip). Deriving from the applied fields rather than the pending
  // `preSelected*` values keeps chips and query in lock-step.
  useEffect(() => {
    if (isRestoring) return;

    const next: SearchChip[] = [];

    (pageState.status ?? []).forEach((value: string) => {
      const option = filterOptions.find((o) => o.value === value);
      next.push({
        label: 'Status',
        field: 'status',
        value,
        displayValue: option?.label ?? value,
        disabled: false,
      });
    });

    if (pageState.startDate) {
      next.push({
        label: 'Start Date',
        field: 'startDate',
        value: new Date(pageState.startDate),
        displayValue: new Date(pageState.startDate).toLocaleDateString(),
      });
    }

    if (pageState.endDate) {
      next.push({
        label: 'End Date',
        field: 'endDate',
        value: new Date(pageState.endDate),
        displayValue: new Date(pageState.endDate).toLocaleDateString(),
      });
    }

    selectedSystems.forEach((s) => {
      next.push({
        label: 'System',
        field: 'system',
        value: s.value,
        displayValue: s.label,
      });
    });

    setChips(next);
  }, [
    isRestoring,
    pageState.status,
    pageState.startDate,
    pageState.endDate,
    selectedSystems,
  ]);

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      resetPagination();
      if (deletedGroup.label === 'Status') {
        if (queryParams.get('status')) {
          deleteQueryParam('status');
        }
        setPageState((prev) => ({
          ...prev,
          status: [],
        }));
        setComponentsSelectedFilters([]);
      }
      if (deletedGroup.label === 'Start Date') {
        setPageState((prev) => ({
          ...prev,
          startDate: '',
        }));
        setPreSelectedStartDate('');
      }
      if (deletedGroup.label === 'End Date') {
        setPageState((prev) => ({
          ...prev,
          endDate: '',
        }));
        setPreSelectedEndDate('');
      }
      if (deletedGroup.label === 'System') {
        setPageState((prev) => ({
          ...prev,
          selectedSystems: [],
        }));
        setPreSelectedSystems([]);
      }
    },
    [deleteQueryParam, queryParams, setPageState, resetPagination]
  );

  const selectedSystemIds = selectedSystems.map((s) => s.value).join(',');

  const { refetch: refetchExportRentals } = useExportRentals(
    {
      queryParameters: {
        order: pageState.sortConfig?.direction,
        status:
          Array.isArray(pageState.status) && pageState.status.length
            ? pageState.status.join(',')
            : undefined,
        pattern: debouncedSearchTerm,
        after_start_time: pageState.startDate
          ? getDateWithoutTime(new Date(pageState.startDate)).toISOString()
          : null,
        before_start_time: pageState.endDate
          ? getDateAtLastSecond(new Date(pageState.endDate)).toISOString()
          : null,
        ...(isOrgMode ? { organization_id: parameters?.organization_id } : {}),
        ...(isOrgMode && selectedSystemIds ? { system_id: selectedSystemIds } : {}),
      },
    },
    {
      enabled: false,
      retry: false,
    }
  );

  const { refetch: refetchExportRentalsRoutes } = useExportSystemRentalsRoutes(
    {
      queryParameters: {
        after_start_time: pageState.startDate
          ? getDateWithoutTime(new Date(pageState.startDate)).toISOString()
          : null,
        before_start_time: pageState.endDate
          ? getDateAtLastSecond(new Date(pageState.endDate)).toISOString()
          : null,
        ...(isOrgMode ? { organization_id: parameters?.organization_id } : {}),
        ...(!isOrgMode ? parameters : {}),
      },
    },
    {
      enabled: false,
      retry: false,
    }
  );

  const handleRentalRouteExport = useCallback(() => {
    CustomAsyncToast({
      // @ts-ignore
      promise: () => refetchExportRentalsRoutes({ throwOnError: true }),
      successMessage: () => 'Rentals exported successfully',
      loadingMessage: 'Exporting rentals...',
      errorMessage: 'Error exporting rentals',
    }).catch(() => {
      // Error is surfaced via the toast above; swallow to avoid an unhandled rejection.
    });
  }, [refetchExportRentalsRoutes]);

  const handleExportRentals = useCallback(() => {
    if (isOrgMode) {
      if (pageState.status.length === 0 && (!pageState.startDate || !pageState.endDate)) {
        CustomToast({
          message: 'Please select status or dates to export rentals',
          type: 'error',
        });
        return;
      }
    }
    CustomAsyncToast({
      // @ts-ignore
      promise: () => refetchExportRentals({ throwOnError: true }),
      successMessage: () => 'Rentals exported successfully',
      loadingMessage: 'Exporting rentals...',
      errorMessage: 'Error exporting rentals',
    }).catch(() => {
      // Error is surfaced via the toast above; swallow to avoid an unhandled rejection.
    });
  }, [
    isOrgMode,
    pageState.status,
    pageState.startDate,
    pageState.endDate,
    refetchExportRentals,
  ]);

  const handleSearchChange = useCallback(
    (search: string) => {
      resetPagination();
      setPageState((prev) => ({
        ...prev,
        search,
      }));
    },
    [setPageState, resetPagination]
  );

  const buttonsMemo = useMemo(() => {
    if (parameters?.user_id) {
      return [
        {
          startIcon: <ShareIcon />,
          label: 'Export Rentals',
          onClick: () => {
            refetchExportRentals();
          },
        },
        {
          startIcon: <AddIcon />,
          label: 'Add',
          onClick: () => onAddRental && onAddRental(),
        },
        {
          startIcon: <ShareIcon />,
          label: 'Export Routes',
          onClick: handleRentalRouteExport,
        },
      ];
    } else {
      return [
        {
          startIcon: <ShareIcon />,
          label: 'Export Routes',
          onClick: handleRentalRouteExport,
        },
        {
          startIcon: <ShareIcon />,
          label: 'Export Rentals',
          onClick: handleExportRentals,
        },
      ];
    }
  }, [
    parameters?.user_id,
    handleRentalRouteExport,
    handleExportRentals,
    onAddRental,
    refetchExportRentals,
  ]);

  const unitTitle =
    isOrgMode && !selectedSystems.length ? 'Hardware' : modeInfo?.details?.unitsTitle;
  const stationTitle =
    isOrgMode && !selectedSystems.length
      ? 'Location'
      : modeInfo?.details?.stationsTitle || '';

  return (
    <Card>
      <PaginationTable
        hasMore={graphqlHasMore}
        totalItems={count}
        isLoading={isLoading || !isComplete || isRestoring || isPaginating}
        isLoadingCache={isRefetching}
        onRowCLick={(id: string) => history.push(`/rentals/${id}`)}
        onSortChange={(sort) => {
          resetPagination();
          setPageState((prev) => ({
            ...prev,
            sortConfig: {
              key: sort.key,
              direction: sort.direction as 'asc' | 'desc',
            },
          }));
        }}
        sortBy={pageState?.sortConfig?.key}
        padding={{ pt: 1, pl: 0, pr: 1 }}
        chips={chips}
        showFilter
        handleChipDelete={handleChipDelete}
        onApplyFilter={() => {
          resetPagination();
          const nextStatus = componentsSelectedFilters.map((item) => item.value);
          setPageState((prev) => ({
            ...prev,
            status: nextStatus,
            startDate: preSelectedStartDate || '',
            endDate: preSelectedEndDate || '',
            ...(isOrgMode ? { selectedSystems: preSelectedSystems } : {}),
          }));
        }}
        spaceFilters={
          <>
            <MovaticFieldGroupMultiSelect
              label="Status"
              value={componentsSelectedFilters}
              onChange={(event: React.SyntheticEvent, newValue: any[]) => {
                setComponentsSelectedFilters(newValue);
              }}
              options={filterOptions}
            />
            <MovaticFieldGroupDate
              label="Start Date"
              value={new Date(preSelectedStartDate) || null}
              onChange={(date) => setPreSelectedStartDate(date)}
            />
            <MovaticFieldGroupDate
              label="End Date"
              value={new Date(preSelectedEndDate) || null}
              onChange={(date) => setPreSelectedEndDate(date)}
            />
            {isOrgMode && (
              <OrganizationSystemMultiSelect
                organization_id={parameters!.organization_id!}
                selectedSystem={preSelectedSystems}
                onChange={(systems: ISystem[]) => {
                  setPreSelectedSystems(
                    systems.map((system) => ({
                      label: system?.name,
                      value: system?.gid,
                      original: system,
                    }))
                  );
                }}
              />
            )}
          </>
        }
        localSearch={false}
        noDataSlice
        columns={
          makeColumns(
            unitTitle,
            stationTitle,
            parameters?.user_id,
            isOrgMode
          ) as IColumn[]
        }
        buttons={buttonsMemo}
        csvFileName={'Rentals.csv'}
        items={rentalsItems}
        searchText={pageState.search}
        handleSearch={(e: React.ChangeEvent<HTMLInputElement>) =>
          handleSearchChange(e.target.value)
        }
        searchPlaceholder={'Search rentals'}
        page={pageNumber}
        rowsPerPage={pageState.sizePerPage}
        onPageChange={(_page: any, newPage: number) => onPageChange(newPage)}
        onRowsPerPageChange={(pageSizeString) => {
          const pageSize = Number(pageSizeString.target.value);
          resetPagination();
          setPageState((prev) => ({
            ...prev,
            sizePerPage: pageSize,
          }));
        }}
      />
    </Card>
  );
};

const RentalR = connect(
  (state: { system: { current: ISystem; isLoaded: boolean }; mode: IMode }) => ({
    system_id: state.system.current.gid,
    systemLoaded: state.system.isLoaded,
  }),
  () => ({})
)(RentalTable);

export default RentalR;
